import React, {useEffect, useId, useRef} from 'react';
import type {WyscoutPlayerFixtureDetail} from '@/features/players/wyscoutFixtureApi';
import WyscoutPlayerMatchContent from './WyscoutPlayerMatchContent';

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const visibleFocusable = (element: HTMLElement): boolean => {
  if (element.tabIndex < 0 || element.matches(':disabled') || element.getAttribute('aria-disabled') === 'true') return false;
  for (let current: HTMLElement | null = element; current; current = current.parentElement) {
    const style = window.getComputedStyle(current);
    if (current.hidden || current.hasAttribute('inert') || current.getAttribute('aria-hidden') === 'true'
      || style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
  }
  return true;
};

export interface WyscoutPlayerMatchDrawerProps {
  open: boolean;
  detail?: WyscoutPlayerFixtureDetail;
  loading?: boolean;
  error?: string | null;
  onClose: () => void;
  onRetry?: () => void;
  fullMatchHref?: string;
  onRestoreFocus?: () => void;
  refreshAnnouncement?: string;
}

const WyscoutPlayerMatchDrawer: React.FC<WyscoutPlayerMatchDrawerProps> = ({
  open,
  detail,
  loading = false,
  error = null,
  onClose,
  onRetry,
  fullMatchHref,
  onRestoreFocus,
  refreshAnnouncement = '',
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const wasOpen = useRef(false);
  const restorationPending = useRef(false);
  const restoreFocusRef = useRef(onRestoreFocus);
  const unmountRestoreTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  restoreFocusRef.current = onRestoreFocus;
  const headingId = useId();
  const title = detail?.fixture.label || 'Player match';

  useEffect(() => {
    if (open && !wasOpen.current) {
      restorationPending.current = true;
      headingRef.current?.focus();
    }
    if (!open && wasOpen.current && restorationPending.current) {
      restorationPending.current = false;
      restoreFocusRef.current?.();
    }
    wasOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (unmountRestoreTimer.current !== undefined) {
      clearTimeout(unmountRestoreTimer.current);
      unmountRestoreTimer.current = undefined;
    }
    return () => {
      unmountRestoreTimer.current = setTimeout(() => {
        if (!restorationPending.current) return;
        restorationPending.current = false;
        restoreFocusRef.current?.();
      }, 0);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open) return;
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !drawerRef.current) return;
      const focusable = Array.from(drawerRef.current.querySelectorAll<HTMLElement>(focusableSelector)).filter(visibleFocusable);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const inside = active instanceof HTMLElement && focusable.includes(active);
      const target = !inside ? (event.shiftKey ? last : first)
        : event.shiftKey && active === first ? last
          : !event.shiftKey && active === last ? first : null;
      if (target) {
        event.preventDefault();
        target.focus();
      } else if (!focusable.length) {
        event.preventDefault();
        drawerRef.current.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, open]);

  const fullMatchLink = fullMatchHref ? (
    <a
      href={fullMatchHref}
      className="inline-flex min-h-11 items-center rounded-md bg-club-blue-700 px-4 text-sm font-bold text-white transition-colors hover:bg-club-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 focus-visible:ring-offset-2 motion-reduce:transition-none dark:focus-visible:ring-offset-gray-900"
    >
      Open full match
    </a>
  ) : null;

  return (
    <>
      {open && (
        <div
          data-testid="player-match-backdrop"
          onClick={onClose}
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
        />
      )}
      <div
        ref={drawerRef}
        role="dialog"
        tabIndex={-1}
        aria-modal={open}
        aria-hidden={!open}
        aria-labelledby={headingId}
        inert={!open}
        className={`fixed inset-y-0 right-0 z-50 flex h-full w-full flex-col bg-white shadow-2xl transition-transform duration-300 ease-out motion-reduce:transition-none motion-reduce:duration-0 dark:bg-gray-900 md:w-[min(720px,56vw)] ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {refreshAnnouncement && <p role="status" aria-live="polite" className="sr-only">{refreshAnnouncement}</p>}
        <header className="sticky top-0 z-20 flex min-h-16 items-center justify-between gap-4 border-b border-gray-200 bg-white px-5 py-3 dark:border-gray-700 dark:bg-gray-900">
          <h1 ref={headingRef} id={headingId} tabIndex={-1} className="min-w-0 truncate text-lg font-bold text-gray-900 outline-none dark:text-gray-100">{title}</h1>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md text-sm font-bold text-gray-700 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 motion-reduce:transition-none dark:text-gray-200 dark:hover:bg-gray-800"
          >
            Close
          </button>
        </header>

        {detail && !loading ? (
          <>
            {error && (
              <p role="alert" className="mx-5 mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
                {error} Cached match details remain visible. {onRetry && <button type="button" className="font-semibold underline" onClick={onRetry}>Retry</button>}
              </p>
            )}
            <WyscoutPlayerMatchContent detail={detail} showHeading={false} footerAction={fullMatchLink} onRetry={onRetry} />
          </>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto p-6">
              {loading ? (
                <div role="status" aria-label="Loading player match" className="space-y-4 animate-pulse motion-reduce:animate-none">
                  <span className="sr-only">Loading player match</span>
                  <div className="h-28 rounded-xl bg-gray-100 dark:bg-gray-800" />
                  <div className="h-40 rounded-xl bg-gray-100 dark:bg-gray-800" />
                  <div className="h-64 rounded-xl bg-gray-100 dark:bg-gray-800" />
                </div>
              ) : error ? (
                <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-5 text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
                  <p className="font-semibold">{error}</p>
                  {onRetry && <button type="button" onClick={onRetry} className="mt-4 min-h-11 rounded-md border border-red-300 px-4 text-sm font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 motion-reduce:transition-none dark:border-red-700">Retry</button>}
                </div>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-300">Match details are not available.</p>
              )}
            </div>
            {fullMatchLink && <footer className="sticky bottom-0 flex min-h-16 items-center justify-end border-t border-gray-200 bg-white px-5 py-3 dark:border-gray-700 dark:bg-gray-900">{fullMatchLink}</footer>}
          </div>
        )}
      </div>
    </>
  );
};

export default WyscoutPlayerMatchDrawer;
